<div class="card border rounded-lg shadow-sm formal-section-editor-card">
  <div class="card-body p-3 d-grid gap-4">
    <div class="d-flex flex-column flex-xl-row justify-content-between gap-3">
      <div>
        <p class="text-uppercase text-muted text-xxs mb-1">
          Section {{ section_index + 1 }}
        </p>
        <h5 class="text-x fw-bold mb-1">{{ section.title || "Untitled section" }}</h5>
        <p class="text-muted text-xxs mb-0">
          Edit one section at a time.
        </p>
      </div>
      <div class="d-flex flex-wrap align-items-start gap-2">
        <button
          class="btn btn-light btn-sm rounded-small text-xxs shadow-sm border text-danger"
          title="Remove section"
          aria-label="Remove section"
          type="button"
          @click="$emit('remove', section.local_id)"
        >
          <i class="fas fa-trash-alt"></i>
        </button>
      </div>
    </div>

    <div class="row g-3">
      <div class="col-12 col-xl-4">
        <label class="form-label text-xxs fw-semibold">Section type</label>
        <select
          :value="section.section_type"
          class="form-select text-xxs rounded-small shadow-sm"
          @change="handle_section_type_change($event.target.value)"
        >
          <option
            v-for="section_type in section_type_options"
            :key="section_type.internal_name"
            :value="section_type.internal_name"
          >
            {{ section_type.display_name }}
          </option>
        </select>
      </div>
      <div class="col-12 col-xl-5">
        <label class="form-label text-xxs fw-semibold">Section title</label>
        <input
          v-model="section.title"
          class="form-control text-xxs rounded-small shadow-sm"
          placeholder="Shown in the builder and preview"
          type="text"
        />
      </div>
    </div>

    <div v-if="section.section_type === 'document_title'" class="row g-3">
      <div class="col-12">
        <label class="form-label text-xxs fw-semibold">Section heading</label>
        <input
          v-model="section.title"
          class="form-control text-xxs rounded-small shadow-sm"
          placeholder="Internal section heading"
          type="text"
        />
      </div>
      <div class="col-12">
        <label class="form-label text-xxs fw-semibold">Document title</label>
        <input
          v-model="section.config.title"
          class="form-control text-xxs rounded-small shadow-sm"
          placeholder="Main title shown in the PDF body"
          type="text"
        />
      </div>
      <div class="col-12">
        <label class="form-label text-xxs fw-semibold">Subtitle</label>
        <input
          v-model="section.config.subtitle"
          class="form-control text-xxs rounded-small shadow-sm"
          placeholder="Supportive subtitle beneath the title"
          type="text"
        />
      </div>
    </div>

    <div v-else-if="section.section_type === 'rich_text'" class="d-grid gap-3">
      <div>
        <label class="form-label text-xxs fw-semibold">Section heading</label>
        <input
          v-model="section.title"
          class="form-control text-xxs rounded-small shadow-sm"
          placeholder="Narrative block title"
          type="text"
        />
      </div>
      <div>
        <label class="form-label text-xxs fw-semibold">Body text</label>
        <textarea
          v-model="section.config.body"
          class="form-control text-xxs rounded-small shadow-sm"
          placeholder="Write the narrative or guidance text for this block"
          rows="7"
        ></textarea>
      </div>
      <div class="card border rounded-small shadow-sm p-2 formal-static-note">
        <p class="text-muted text-xxs mb-0">
          Rich text is static copy in this first pass. Use placeholders like
          <span class="fw-semibold">&#123;&#123; entity_name &#125;&#125;</span> in the text if needed.
        </p>
      </div>
    </div>

    <div v-else-if="section.section_type === 'field_list'" class="d-grid gap-3">
      <div class="d-flex justify-content-between align-items-center gap-2">
        <div>
          <label class="form-label text-xxs fw-semibold mb-1">Field list items</label>
          <p class="text-muted text-xxs mb-0">
            Each row shows one label and one value.
          </p>
        </div>
        <div class="d-flex flex-column align-items-end gap-1">
          <div class="d-flex flex-wrap justify-content-end align-items-center gap-2">
            <button
              class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark"
              type="button"
              @click="open_bulk_question_picker('field_items_bulk')"
            >
              Manage Questions
            </button>
            <span class="badge bg-light text-dark text-xxs rounded-small shadow-sm px-2 py-2">
              {{ get_bulk_question_count("field_items_bulk") }} linked
            </span>
            <button
              class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark"
              title="Add blank field"
              aria-label="Add blank field"
              type="button"
              @click="add_field_item"
            >
              <i class="fas fa-plus"></i>
            </button>
          </div>
          <p class="text-muted text-xxs mb-0 text-end">
            Choose Question in Source or use Manage Questions for onboarding or compliance answers not available in the system-field list.
          </p>
        </div>
      </div>

      <div
        v-for="item in get_field_items()"
        :key="item.local_id"
        class="card border rounded-small shadow-sm p-3"
      >
        <div class="row g-3">
          <div class="col-12 col-xl-3">
            <label class="form-label text-xxs fw-semibold">Label</label>
            <input
              v-model="item.label"
              class="form-control text-xxs rounded-small shadow-sm"
              :placeholder="get_label_placeholder(item)"
              type="text"
            />
          </div>
          <div class="col-12 col-xl-3">
            <label class="form-label text-xxs fw-semibold">Source</label>
            <select
              :value="item.binding.source_type"
              class="form-select text-xxs rounded-small shadow-sm"
              @change="handle_field_item_source_selection(item, $event.target.value)"
            >
              <option value="system_field">System field</option>
              <option value="section_question">Question</option>
              <option value="static_text">Static text</option>
            </select>
          </div>
          <div
            v-if="item.binding.source_type === 'system_field'"
            class="col-12 col-xl-4"
          >
            <label class="form-label text-xxs fw-semibold">System field</label>
            <select
              v-model="item.binding.system_field"
              class="form-select text-xxs rounded-small shadow-sm"
              @change="handle_label_target_change(item)"
            >
              <option
                v-for="option in system_field_options"
                :key="option.id"
                :value="option.id"
              >
                {{ option.label }}
              </option>
            </select>
          </div>
          <div
            v-else-if="item.binding.source_type === 'section_question'"
            class="col-12 col-xl-4"
          >
            <label class="form-label text-xxs fw-semibold">Question</label>
            <div class="card border rounded-small shadow-sm p-2 d-grid gap-2">
              <div class="text-xxs text-muted">
                {{ get_question_label(item.binding.section_question_id) }}
              </div>
              <p class="text-muted text-xxs mb-0">
                Use Question in Source or Manage Questions above to add, remove, or reorder linked questions.
              </p>
            </div>
          </div>
          <div v-else class="col-12 col-xl-4">
            <label class="form-label text-xxs fw-semibold">Static text</label>
            <input
              v-model="item.binding.value"
              class="form-control text-xxs rounded-small shadow-sm"
              placeholder="Text shown in the report"
              type="text"
            />
          </div>
          <div class="col-12 col-xl-2 d-flex align-items-end">
            <button
              class="btn btn-light btn-sm rounded-small text-xxs shadow-sm border text-danger"
              title="Remove field"
              aria-label="Remove field"
              type="button"
              @click="remove_field_item(item.local_id)"
            >
              <i class="fas fa-trash-alt"></i>
            </button>
          </div>
        </div>
      </div>
    </div>

    <div v-else-if="section.section_type === 'checklist_matrix'" class="d-grid gap-3">
      <div class="d-flex justify-content-between align-items-center gap-2">
        <div>
          <label class="form-label text-xxs fw-semibold mb-1">Checklist rows</label>
          <p class="text-muted text-xxs mb-0">
            Link each row to a question.
          </p>
        </div>
        <div class="d-flex flex-column align-items-end gap-1">
          <div class="d-flex flex-wrap justify-content-end align-items-center gap-2">
            <button
              class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark"
              type="button"
              @click="open_bulk_question_picker('checklist_rows_bulk')"
            >
              Manage Questions
            </button>
            <span class="badge bg-light text-dark text-xxs rounded-small shadow-sm px-2 py-2">
              {{ get_bulk_question_count("checklist_rows_bulk") }} linked
            </span>
            <button
              class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark"
              title="Add blank checklist row"
              aria-label="Add blank checklist row"
              type="button"
              @click="add_checklist_row"
            >
              <i class="fas fa-plus"></i>
            </button>
          </div>
          <p class="text-muted text-xxs mb-0 text-end">
            Use Manage Questions for onboarding or compliance rows. Use + when you need a manual checklist line.
          </p>
        </div>
      </div>

      <div class="card border rounded-small shadow-sm p-3">
        <div class="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-2 mb-3">
          <div>
            <label class="form-label text-xxs fw-semibold mb-1">Checklist columns</label>
            <p class="text-muted text-xxs mb-0">
              Choose what the checklist should show.
            </p>
          </div>
          <span class="badge bg-light text-dark text-xxs rounded-small shadow-sm px-2 py-2">
            {{ get_checklist_display_fields().length }} selected
          </span>
        </div>
        <div class="row g-2">
          <div
            v-for="field_option in checklist_field_options"
            :key="field_option.internal_name"
            class="col-12 col-md-6"
          >
            <label class="card border rounded-small shadow-sm p-2 h-100">
              <div class="d-flex align-items-start gap-2">
                <input
                  :checked="is_checklist_display_field_selected(field_option.internal_name)"
                  class="form-check-input shadow-sm"
                  type="checkbox"
                  @change="toggle_checklist_display_field(field_option.internal_name, $event.target.checked)"
                />
                <div class="d-grid gap-1">
                  <span class="fw-semibold text-dark text-xxs">
                    {{ field_option.display_name }}
                  </span>
                  <span class="text-muted text-xxs">
                    {{ get_checklist_field_description(field_option.internal_name) }}
                  </span>
                </div>
              </div>
            </label>
          </div>
        </div>
      </div>

      <div
        v-for="row in get_checklist_rows()"
        :key="row.local_id"
        class="card border rounded-small shadow-sm p-3"
      >
        <div class="row g-3">
          <div class="col-12 col-xl-3">
            <label class="form-label text-xxs fw-semibold">Row label</label>
            <input
              v-model="row.label"
              class="form-control text-xxs rounded-small shadow-sm"
              :placeholder="get_label_placeholder(row)"
              type="text"
            />
          </div>
          <div class="col-12 col-xl-7">
            <label class="form-label text-xxs fw-semibold">Question</label>
            <div class="card border rounded-small shadow-sm p-2 d-grid gap-2">
              <div class="text-xxs text-muted">
                {{ get_question_label(row.binding?.section_question_id) }}
              </div>
              <p class="text-muted text-xxs mb-0">
                Use Manage Questions above to add, remove, or reorder linked questions.
              </p>
            </div>
          </div>
          <div class="col-12 col-xl-2 d-flex align-items-end">
            <button
              class="btn btn-light btn-sm rounded-small text-xxs shadow-sm border text-danger"
              title="Remove checklist row"
              aria-label="Remove checklist row"
              type="button"
              @click="remove_checklist_row(row.local_id)"
            >
              <i class="fas fa-trash-alt"></i>
            </button>
          </div>
        </div>
      </div>
    </div>

    <FormalDocumentTemplateDataTableEditor
      v-else-if="is_data_table"
      :question_options="question_options"
      :section="section"
      :system_field_options="system_field_options"
      @open-question-picker="$emit('open-question-picker', $event)"
    />

    <div v-else class="d-grid gap-3">
      <div class="d-flex justify-content-between align-items-center gap-2">
        <div>
          <label class="form-label text-xxs fw-semibold mb-1">Signatories</label>
          <p class="text-muted text-xxs mb-0">
            Add the people or roles that should appear in the sign-off area.
          </p>
        </div>
        <button
          class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark"
          type="button"
          @click="add_signatory"
        >
          Add Signatory
        </button>
      </div>

      <div
        v-for="signatory in get_signatories()"
        :key="signatory.local_id"
        class="card border rounded-small shadow-sm p-3"
      >
        <div class="row g-3">
          <div class="col-12 col-xl-3">
            <label class="form-label text-xxs fw-semibold">Signatory type</label>
            <select
              :value="signatory.signatory_type"
              class="form-select text-xxs rounded-small shadow-sm"
              @change="handle_signatory_type_change(signatory, $event.target.value)"
            >
              <option
                v-for="option in signatory_type_options"
                :key="option.internal_name"
                :value="option.internal_name"
              >
                {{ option.display_name }}
              </option>
            </select>
          </div>
          <div class="col-12 col-xl-4">
            <label class="form-label text-xxs fw-semibold">Role</label>
            <input
              v-model="signatory.role"
              class="form-control text-xxs rounded-small shadow-sm"
              placeholder="Role or designation"
              type="text"
            />
          </div>
          <div
            v-if="!is_admin_signature_signatory(signatory)"
            class="col-12 col-xl-3"
          >
            <label class="form-label text-xxs fw-semibold">Name</label>
            <input
              v-model="signatory.name"
              class="form-control text-xxs rounded-small shadow-sm"
              placeholder="Name shown on the report"
              type="text"
            />
          </div>
          <div
            v-else
            class="col-12 col-xl-3"
          >
            <label class="form-label text-xxs fw-semibold">Generating admin</label>
            <div class="card border rounded-small shadow-sm p-2 h-100 d-flex justify-content-center">
              <p class="text-muted text-xxs mb-0">
                The generating admin's name, signature, and signed time will be captured before the PDF is created.
              </p>
            </div>
          </div>
          <div class="col-12 col-xl-2 d-flex align-items-end">
            <button
              class="btn btn-light btn-sm rounded-small text-xxs shadow-sm border text-danger"
              title="Remove signatory"
              aria-label="Remove signatory"
              type="button"
              @click="remove_signatory(signatory.local_id)"
            >
              <i class="fas fa-trash-alt"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
